/**
 * Decodes a JWT to extract its expiration time, in milliseconds since epoch.
 * Returns null when the token is absent, is not a JWT, or carries no `exp`.
 */
export const getTokenExpiration = (
  token: string | null | undefined,
): number | null => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part). The length check above guarantees it
    // exists, but strict index checking in some workspaces cannot see that.
    const payload = parts[1];
    if (!payload) {
      return null;
    }

    // Add padding if necessary for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(
      paddedPayload.replace(/-/g, '+').replace(/_/g, '/'),
    );

    const parsedPayload = JSON.parse(decodedPayload);

    // JWT exp field is in seconds since epoch, convert to milliseconds
    if (parsedPayload.exp && typeof parsedPayload.exp === 'number') {
      return parsedPayload.exp * 1000;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Whether a JWT is expired or will expire within `bufferMinutes`. A token
 * whose expiration cannot be read is reported as expired.
 */
export const isTokenExpiringSoon = (
  token: string | null | undefined,
  bufferMinutes = 5,
): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true; // If we can't read expiration, consider it expired
  }

  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() + bufferMs >= expiration;
};

/**
 * The token's expiration, falling back to `fallbackMinutes` from now when the
 * JWT cannot be decoded.
 */
export const getTokenExpirationWithFallback = (
  token: string | null | undefined,
  fallbackMinutes = 60,
): number => {
  const jwtExpiration = getTokenExpiration(token);

  if (jwtExpiration) {
    return jwtExpiration;
  }

  // Fallback to hard-coded value if JWT decode fails
  return Date.now() + fallbackMinutes * 60 * 1000;
};
